
import { useAuthStore } from "@/stores/authStore";
import { SecurityTab } from "@/components/profile/tabs/SecurityTab";
import { DangerZoneTab } from "@/components/profile/tabs/DangerZoneTab";
import { Separator } from "@/components/ui/separator";
import { useState } from "react";
import { useProfile } from "@/hooks/useProfile";
import { AccountStatusButton } from "@/components/settings/AccountStatusButton";
import { DeactivationDialog } from "@/components/settings/DeactivationDialog";
import { DeactivationWarning } from "@/components/settings/DeactivationWarning";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function AccountSettings() {
  const { user, signOut } = useAuthStore();
  const { profile } = useProfile(user);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);

  const handleDeactivateAccount = async () => {
    try {
      const deactivationDate = new Date();
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          id: user?.id,
          is_active: false,
          updated_at: deactivationDate.toISOString()
        })
        .eq('id', user?.id);

      if (error) throw error;

      toast.success("Account deactivated successfully");
      signOut();
    } catch (error) {
      console.error('Error deactivating account:', error);
      toast.error("Failed to deactivate account");
    }
  };

  const handleReactivateAccount = async () => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ 
          id: user?.id,
          is_active: true 
        })
        .eq('id', user?.id);

      if (error) throw error;

      toast.success("Account reactivated successfully");
    } catch (error) {
      console.error('Error reactivating account:', error);
      toast.error("Failed to reactivate account");
    }  
  };

  const canSelfReactivate = () => {
    if (!profile?.is_active) {
      return true; // Simplified reactivation logic - always allow reactivation
    }
    return false;
  };

  if (!user || !profile) return null;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium">Account Settings</h3>
        <p className="text-sm text-muted-foreground">
          Manage your account security and preferences
        </p>
      </div>

      {!profile.is_active && !canSelfReactivate() && <DeactivationWarning />}

      <div className="mt-6">
        <SecurityTab user={user} />
        <Separator className="my-6" />
        
        <div className="space-y-4">
          <div className="rounded-lg border p-4 md:p-6 bg-destructive/5">
            <h4 className="text-lg font-medium text-destructive">Account Status</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Deactivating your account will temporarily disable access. You can reactivate within 7 days, after which admin assistance will be required.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <AccountStatusButton
                isActive={profile.is_active}
                canSelfReactivate={canSelfReactivate()}
                onDeactivate={() => setShowDeactivateDialog(true)}
                onReactivate={handleReactivateAccount}
              />
            </div>
          </div>
        </div>
      </div>

      <DeactivationDialog
        open={showDeactivateDialog}
        onOpenChange={setShowDeactivateDialog}
        onConfirm={handleDeactivateAccount}
      />
    </div>
  );
}
